import React, { useEffect, useState } from "react";

import { Header, Menu, Message, Segment, Table } from "semantic-ui-react";

import DateRangePicker from "@wojtekmaj/react-daterange-picker/dist/DateRangePicker";
import DownloadDropdownMenuItem from "components/features/DownloadDropdownMenuItem";

import useProductParser from "hooks/useProductParser";

import { parseDate } from "utilities/date";
import { format } from "date-fns";
import Alerts from "utilities/Alerts";
import http from "utilities/http";

function Ledger() {
	const oneDay = 1000 * 60 * 60 * 24;
	const currentDate = Date.now();
	const oldDate = currentDate - oneDay * 1; // 1 week old

	const [captureDate, setCaptureDate] = useState([
		new Date(oldDate),
		new Date(currentDate),
	]);
	const [records, setRecords] = useState([]);
	const [ledger, setLedger] = useState([]);
	const [loading, setLoading] = useState(false);

	const { parseProductName } = useProductParser();

	useEffect(() => {
		const formatRecord = ({
			date = null,
			productId = "",
			openingQuantity = "-",
			openingRate = "-",
			stockInQuantity = "-",
			stockInRate = "-",
			stockOutQuantity = "-",
			stockOutRate = "-",
			closingQuantity = "-",
			closingRate = "-",
		}) => ({
			date: format(new Date(date), "yyyy-MM-dd"),
			productId,
			openingQuantity,
			openingRate,
			openingAmount:
				openingQuantity !== "-"
					? parseFloat(openingQuantity) * parseFloat(openingRate)
					: "-",
			stockInQuantity,
			stockInRate,
			stockInAmount:
				stockInQuantity !== "-"
					? parseFloat(stockInQuantity) * parseFloat(stockInRate)
					: "-",
			stockOutQuantity,
			stockOutRate,
			stockOutAmount:
				stockOutQuantity !== "-"
					? parseFloat(stockOutQuantity) * parseFloat(stockOutRate)
					: "-",
			closingQuantity,
			closingRate,
			closingAmount:
				closingQuantity !== "-"
					? parseFloat(closingQuantity) * parseFloat(closingRate)
					: "-",
		});
		const rows = [];

		for (const rc of records) {
			const { date, dayRecords } = rc;

			for (const prodWise of dayRecords) {
				const {
					productId,
					opening,
					stockIns,
					stockOuts,
					closing,
				} = prodWise;

				opening.forEach((item) => {
					rows.push(
						formatRecord({
							date,
							productId,
							openingQuantity: item.quantity,
							openingRate: item.rate,
						})
					);
				});
				stockIns.forEach((item) =>
					rows.push(
						formatRecord({
							date,
							productId,
							stockInQuantity: item.quantity,
							stockInRate: item.rate,
						})
					)
				);
				stockOuts.forEach((item) =>
					rows.push(
						formatRecord({
							date,
							productId,
							stockOutQuantity: item.quantity,
							stockOutRate: item.rate,
						})
					)
				);
				closing.forEach((item) =>
					rows.push(
						formatRecord({
							date,
							productId,
							closingQuantity: item.quantity,
							closingRate: item.rate,
						})
					)
				);
			}
		}

		setLedger(rows);
	}, [records]);

	useEffect(() => {
		const fetchLedgerData = async (dates) => {
			try {
				const [startDate, endDate] = dates.map((item) =>
					parseDate(item)
				);

				setLoading(true);

				const response = await http.get(
					`/report/ledger/date/${startDate}/${endDate}`
				);

				const { records } = response.data;

				setRecords(records);
			} catch (ex) {
				Alerts.httpException(ex);
			} finally {
				setLoading(false);
			}
		};

		fetchLedgerData(captureDate);
	}, [captureDate]);

	return (
		<>
			<Menu
				borderless
				style={{ position: "sticky", top: "0px", zIndex: "99" }}
			>
				<Menu.Item>
					<Header content="Stock Ledger" />
				</Menu.Item>

				<Menu.Menu position="right">
					<Menu.Item>
						<span
							style={{
								fontWeight: "700",
								fontSize: "16px",
								color: "grey",
								paddingRight: "10px",
							}}
						>
							Showing results for:
						</span>
						<DateRangePicker
							maxDate={new Date()}
							value={captureDate}
							onChange={(e) => setCaptureDate(e)}
						/>
					</Menu.Item>
					<Menu.Item>
						<DownloadDropdownMenuItem />
					</Menu.Item>
				</Menu.Menu>
			</Menu>
			<Segment
				loading={loading}
				style={{ width: "inherit", overflow: "auto", padding: 0 }}
			>
				{!ledger.length ? <Message content="No record found." /> : null}
				{ledger.length ? (
					<Table celled structured singleLine>
						<Table.Header>
							<Table.Row>
								<Table.HeaderCell rowSpan={2}>
									Date
								</Table.HeaderCell>
								<Table.HeaderCell rowSpan={2}>
									Product
								</Table.HeaderCell>
								<Table.HeaderCell
									colSpan={3}
									textAlign="center"
								>
									Opening
								</Table.HeaderCell>
								<Table.HeaderCell
									colSpan={3}
									textAlign="center"
								>
									Stock In
								</Table.HeaderCell>
								<Table.HeaderCell
									colSpan={3}
									textAlign="center"
								>
									Stock Out
								</Table.HeaderCell>
								<Table.HeaderCell
									colSpan={3}
									textAlign="center"
								>
									Closing
								</Table.HeaderCell>
							</Table.Row>
							<Table.Row>
								<Table.HeaderCell style={{ width: "100px" }}>
									Qty.
								</Table.HeaderCell>
								<Table.HeaderCell style={{ width: "200px" }}>
									Rate
								</Table.HeaderCell>
								<Table.HeaderCell style={{ width: "220px" }}>
									Amount
								</Table.HeaderCell>
								<Table.HeaderCell style={{ width: "100px" }}>
									Qty.
								</Table.HeaderCell>
								<Table.HeaderCell style={{ width: "200px" }}>
									Rate
								</Table.HeaderCell>
								<Table.HeaderCell style={{ width: "220px" }}>
									Amount
								</Table.HeaderCell>
								<Table.HeaderCell style={{ width: "100px" }}>
									Qty.
								</Table.HeaderCell>
								<Table.HeaderCell style={{ width: "200px" }}>
									Rate
								</Table.HeaderCell>
								<Table.HeaderCell style={{ width: "220px" }}>
									Amount
								</Table.HeaderCell>
								<Table.HeaderCell style={{ width: "100px" }}>
									Qty.
								</Table.HeaderCell>
								<Table.HeaderCell style={{ width: "200px" }}>
									Rate
								</Table.HeaderCell>
								<Table.HeaderCell style={{ width: "220px" }}>
									Amount
								</Table.HeaderCell>
							</Table.Row>
						</Table.Header>

						<Table.Body>
							{ledger.map((item, i) => (
								<Table.Row key={item.date + item.productId + i}>
									{Object.keys(item).map((val, ii) => (
										<Table.Cell
											key={
												item.date +
												item.productId +
												val +
												ii
											}
										>
											{val === "productId"
												? parseProductName(item[val])
												: item[val]}
										</Table.Cell>
									))}
								</Table.Row>
							))}
						</Table.Body>
					</Table>
				) : null}
			</Segment>
		</>
	);
}

export default Ledger;
